import { faLocationDot } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { ICSVRow, IRootState } from "app/model"
import { getStore } from "app/store/store"
import { getActiveAppContext } from "app/tools/common"
import { readCsvFile } from "app/tools/file"
import { ICoordinateData } from "coordinate/model"
import { coordinateRPO } from "coordinate/repository"
import { notifyCoordinatesSet } from "coordinate/tools/common"
import {
  GEO_TARGETS_TYPES,
  IForm,
  IGeoTargetsFileImport,
  REQUIRED_COORDINATE_CSV_COLUMNS,
  REQUIRED_POSTAL_CODE_CSV_COLUMNS
} from "form/model"
import { formActionCreator } from "form/store/actions"
import { getDefaultGeoTargetsFileImportState, getFormData } from "form/store/state"
import { messageSVC } from "message/service"
import { SHAPE_TYPES } from "shape/model"
import { shapeRPO } from "shape/repository"
import { notifyShapesSet } from "shape/tools/common"
import { getForm } from "./common"
import { getGeoTargetsChanges, isValidLatitude, isValidLongitude, isValidRadius } from "./geo-targets"
import { getCoordinateRadiusInMeter, getNewFormForCoordinates } from "./geo-targets-coordinates"
import { getNewFormForShapes } from "./geo-targets-shapes"

export function getGeoTargetsFileImport(state?: IRootState): IGeoTargetsFileImport {
  return getForm(state).geoTargets.fileImport
}

export function selectGeoTargetsFileImportFiles(files: FileList | null): void {
  const activeContext = getActiveAppContext()
  if (!activeContext) {
    throw Error("No active context")
  }
  const { isParsing, isSaving, geoTargetsType } = getGeoTargetsFileImport()
  if (isParsing || isSaving) {
    return
  }
  if (files === null) {
    getStore().dispatch(
      formActionCreator.setGeoTargetsFileImport(activeContext, {
        files,
        isParsing: false,
        parsingError: "",
        rows: null
      })
    )
    return
  }
  getStore().dispatch(
    getStore().dispatch(
      formActionCreator.setGeoTargetsFileImport(activeContext, {
        files,
        isParsing: true,
        parsingError: "",
        rows: null
      })
    )
  )
  readCsvFile(files)
    .then(csvRows => {
      const firstCsvRow = csvRows[0]
      let parsingError = ""
      let rows: ICoordinateData[] | string[] = []
      if (!firstCsvRow) {
        parsingError = translate("campaign.geo-targets.file.import.error.no.row")
      } else {
        const columns = Object.keys(firstCsvRow)
        let requiredColumns: string[]
        switch (geoTargetsType) {
          case GEO_TARGETS_TYPES.COORDINATE: {
            requiredColumns = REQUIRED_COORDINATE_CSV_COLUMNS
            break
          }
          case GEO_TARGETS_TYPES.POSTAL_CODE: {
            requiredColumns = REQUIRED_POSTAL_CODE_CSV_COLUMNS
            break
          }
          default: {
            throw Error(`Type "${geoTargetsType}" not supported`)
          }
        }
        for (const requiredColumn of requiredColumns) {
          if (!columns.includes(requiredColumn)) {
            parsingError = translate("campaign.geo-targets.file.import.error.missing.column", [
              requiredColumn,
              requiredColumns.join(", ")
            ])
            break
          }
        }
      }
      if (!parsingError) {
        let result
        switch (geoTargetsType) {
          case GEO_TARGETS_TYPES.COORDINATE: {
            result = typeAndCheckCoordinateCsvData(csvRows)
            break
          }
          case GEO_TARGETS_TYPES.POSTAL_CODE: {
            result = typeAndCheckPostalCodeCsvData(csvRows)
            break
          }
          default: {
            throw Error(`Type "${geoTargetsType}" not supported`)
          }
        }
        if (typeof result === "string") {
          parsingError = result
        } else {
          rows = result
        }
      }
      getStore().dispatch(
        formActionCreator.setGeoTargetsFileImport(activeContext, {
          rows,
          isParsing: false,
          parsingError
        })
      )
    })
    .catch((error: Error) => {
      getStore().dispatch(
        formActionCreator.setGeoTargetsFileImport(activeContext, {
          rows: [],
          isParsing: false,
          parsingError: error.message
            ? error.message.indexOf(".") !== error.message.lastIndexOf(".") // it's a translation key
              ? translate(error.message)
              : error.message
            : String(error)
        })
      )
    })
}

export function setGeoTargetsFileImportType(type: "insert" | "replace"): void {
  const activeContext = getActiveAppContext()
  if (!activeContext) {
    throw Error("No active context")
  }
  getStore().dispatch(formActionCreator.setGeoTargetsFileImport(activeContext, { importType: type }))
}

export function setGeoTargetsFileImportError(error: string): void {
  const activeContext = getActiveAppContext()
  if (!activeContext) {
    throw Error("No active context")
  }
  getStore().dispatch(formActionCreator.setGeoTargetsFileImport(activeContext, { error }))
}

export function setGeoTargetsFileImportPopupVisibility(visibility: boolean, type: GEO_TARGETS_TYPES): void {
  const activeContext = getActiveAppContext()
  if (!activeContext) {
    throw Error("No active context")
  }
  if (!visibility) {
    const { isSaving } = getGeoTargetsFileImport()
    if (isSaving) {
      return
    }
    getStore().dispatch(formActionCreator.setGeoTargetsFileImport(activeContext, getDefaultGeoTargetsFileImportState(type)))
  } else {
    getStore().dispatch(
      formActionCreator.setGeoTargetsFileImport(activeContext, {
        geoTargetsType: type,
        isPopupOpened: true,
        rows: null
      })
    )
  }
}

export const importGeoTargetsFile = (): void => {
  const activeContext = getActiveAppContext()
  if (!activeContext) {
    throw Error("No active context")
  }
  const coordinateImport = getGeoTargetsFileImport()
  const { rows, isSaving, geoTargetsType } = coordinateImport
  if (isSaving) {
    messageSVC.error(translate("campaign.geo-targets.file.import.error.save.in.progress"))
    return
  }
  if (!rows) {
    messageSVC.error(translate("campaign.geo-targets.file.import.error.no.rows"))
    return
  }
  const form = getForm()
  let existingData: any[]
  switch (geoTargetsType) {
    case GEO_TARGETS_TYPES.COORDINATE: {
      existingData = form.geoTargets.coordinates
      break
    }
    case GEO_TARGETS_TYPES.POSTAL_CODE: {
      existingData = form.geoTargets.postalCodes
      break
    }
    default: {
      throw Error(`Type "${geoTargetsType}" not supported`)
    }
  }
  const importType = existingData.length === 0 ? "" : coordinateImport.importType
  messageSVC.confirmMessage({
    icon: faLocationDot,
    title: translate(`campaign.geo-targets.${geoTargetsType}.import.confirm.title`),
    addGenericConfirmMessage: true,
    message: translate(
      !importType
        ? `campaign.geo-targets.${geoTargetsType}.import.confirm.add.message`
        : importType === "replace"
          ? `campaign.geo-targets.${geoTargetsType}.import.confirm.replace.message`
          : `campaign.geo-targets.${geoTargetsType}.import.confirm.insert.message`,
      rows.length
    ),
    onSuccess: async () => {
      getStore().dispatch(formActionCreator.setGeoTargetsFileImport(activeContext, { isSaving: true }))
      try {
        switch (geoTargetsType) {
          case GEO_TARGETS_TYPES.COORDINATE: {
            await processImportCoordinates(rows as ICoordinateData[], form, importType === "insert")
            break
          }
          case GEO_TARGETS_TYPES.POSTAL_CODE: {
            await processImportPostalCodes(rows as string[], form, importType === "insert")
            break
          }
          default: {
            throw Error(`Type "${geoTargetsType}" not supported`)
          }
        }
        getStore().dispatch(formActionCreator.setGeoTargetsFileImport(activeContext, getDefaultGeoTargetsFileImportState()))
      } catch (error) {
        console.error(error)
        getStore().dispatch(
          formActionCreator.setGeoTargetsFileImport(activeContext, {
            isSaving: false,
            error: translate("campaign.geo-targets.file.import.error.unexpected")
          })
        )
      }
    }
  })
}

async function processImportCoordinates(rows: ICoordinateData[], form: IForm, isInsert: boolean) {
  const rowsWithRadiusInMeters = rows.map(r => ({
    ...r,
    radius: getCoordinateRadiusInMeter(r.radius.toString())
  }))
  const toSetCoordinates = isInsert
    ? [
        ...form.geoTargets.coordinates.map(c => ({
          name: c.name,
          latitude: c.latitude,
          longitude: c.longitude,
          radius: c.radius
        })),
        ...rowsWithRadiusInMeters
      ]
    : rowsWithRadiusInMeters
  const newCoordinates = await coordinateRPO.setCoordinates(form.tempCampaignId, toSetCoordinates)
  const newForm = getNewFormForCoordinates(form, newCoordinates.slice(), newCoordinates)
  getStore().dispatch(
    formActionCreator.setForm({
      form: newForm,
      lastSavedData: getFormData(form),
      changes: {
        geoTargets: getGeoTargetsChanges(newForm)
      }
    })
  )
  notifyCoordinatesSet(newCoordinates)
}

async function processImportPostalCodes(rows: string[], form: IForm, isInsert: boolean) {
  const postalCodes = isInsert ? form.geoTargets.postalCodes.map(p => p.postal_code) : rows
  if (isInsert) {
    for (const row of rows) {
      if (postalCodes.includes(row)) {
        continue
      }
      postalCodes.push(row)
    }
  }
  await shapeRPO.setPostalCodesToCampaign(
    form.tempCampaignId,
    postalCodes.filter(p => p !== "")
  )
  const newPostalCodes = await shapeRPO.getAssociatedPostalCodes(form.tempCampaignId)
  const newForm = getNewFormForShapes(form, SHAPE_TYPES.POSTAL_CODE, newPostalCodes, newPostalCodes)
  getStore().dispatch(
    formActionCreator.setForm({
      form: newForm,
      lastSavedData: getFormData(form),
      changes: {
        geoTargets: getGeoTargetsChanges(newForm)
      }
    })
  )
  notifyShapesSet({ shapes: newPostalCodes, type: SHAPE_TYPES.POSTAL_CODE })
}

function typeAndCheckCoordinateCsvData(rows: ICSVRow[]): string | ICoordinateData[] {
  const result: ICoordinateData[] = []
  for (let i = 0; i < rows.length; i++) {
    const { name, latitude, longitude, radius } = rows[i]
    if (!name) {
      //  + 2 => add first column line && index start at zero
      return translate("campaign.geo-targets.coordinate.import.error.line.name", i + 2)
    }
    if (!isValidLatitude(latitude)) {
      return translate("campaign.geo-targets.coordinate.import.error.line.latitude", i + 2)
    }
    if (!isValidLongitude(longitude)) {
      return translate("campaign.geo-targets.coordinate.import.error.line.longitude", i + 2)
    }
    if (!isValidRadius(radius)) {
      return translate("campaign.geo-targets.coordinate.import.error.line.radius", i + 2)
    }
    result.push({
      name,
      latitude: Number(latitude),
      longitude: Number(longitude),
      radius: Number(radius)
    })
  }
  return result
}

function typeAndCheckPostalCodeCsvData(rows: ICSVRow[]): string | string[] {
  const result: string[] = []
  for (let i = 0; i < rows.length; i++) {
    const { postal_code } = rows[i]
    if (!postal_code) {
      //  + 2 => add first column line && index start at zero
      return translate("campaign.geo-targets.postal-code.import.error.line", i + 2)
    }
    result.push(postal_code)
  }
  return result
}
